import React, { useState, useRef, useEffect } from 'react';
import { Palette, Check, X, RefreshCw } from 'lucide-react';

/**
 * Enhanced Color Picker Component
 *
 * Features:
 * - Visual color palette
 * - Hex input
 * - Recent colors
 * - Brand color presets
 * - Custom color selection
 */
const EnhancedColorPicker = ({
    label,
    value,
    onChange,
    presetColors = [],
    showRecent = true
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [customColor, setCustomColor] = useState(value || '#000000');
    const [recentColors, setRecentColors] = useState([]);
    const pickerRef = useRef(null);

    // Brand color presets
    const brandColors = [
        { name: 'Primary', color: '#2C1810' },
        { name: 'Primary Light', color: '#4A2F23' },
        { name: 'Primary Dark', color: '#1A0F0A' },
        { name: 'Accent', color: '#C9A882' },
        { name: 'Accent Light', color: '#E5D4B8' },
        { name: 'Accent Dark', color: '#A68B6A' },
        { name: 'White', color: '#FFFFFF' },
        { name: 'Black', color: '#000000' },
        { name: 'Gray 100', color: '#F3F4F6' },
        { name: 'Gray 200', color: '#E5E7EB' },
        { name: 'Gray 500', color: '#6B7280' },
        { name: 'Gray 900', color: '#111827' }
    ];

    // Common colors palette
    const commonColors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
        '#DFE6E9', '#74B9FF', '#A29BFE', '#FD79A8', '#FDCB6E',
        '#E17055', '#00B894', '#00CEC9', '#0984E3', '#6C5CE7',
        '#B2BEC3', '#636E72', '#2D3436', '#F8F9FA', '#E9ECEF'
    ];

    // All colors (brand + common + custom presets)
    const allPresetColors = [
        ...brandColors.map(c => c.color),
        ...commonColors,
        ...presetColors
    ];

    // Close picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Load recent colors from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('recentColors');
        if (saved) {
            setRecentColors(JSON.parse(saved));
        }
    }, []);

    // Save color to recent colors
    const saveToRecent = (color) => {
        const updated = [color, ...recentColors.filter(c => c !== color)].slice(0, 10);
        setRecentColors(updated);
        localStorage.setItem('recentColors', JSON.stringify(updated));
    };

    // Handle color selection
    const handleColorSelect = (color) => {
        onChange(color);
        setCustomColor(color);
        saveToRecent(color);
        setIsOpen(false);
    };

    // Handle custom hex input
    const handleCustomColorChange = (e) => {
        const color = e.target.value;
        setCustomColor(color);
        if (/^#[0-9A-F]{6}$/i.test(color)) {
            onChange(color);
            saveToRecent(color);
        }
    };

    return (
        <div className="relative" ref={pickerRef}>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-gray-700)' }}>
                {label}
            </label>

            {/* Color Display Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center gap-3 px-4 py-3 border border-gray-300 hover:border-gray-400 transition-colors"
                style={{
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--color-white)'
                }}
            >
                <div
                    className="w-8 h-8 border-2 border-gray-300 flex-shrink-0"
                    style={{
                        backgroundColor: value || '#000000',
                        borderRadius: 'var(--radius-sm)'
                    }}
                />
                <span className="flex-1 text-left font-mono text-sm" style={{ color: 'var(--color-gray-900)' }}>
                    {value || '#000000'}
                </span>
                <Palette className="w-5 h-5" style={{ color: 'var(--color-gray-500)' }} />
            </button>

            {/* Color Picker Dropdown */}
            {isOpen && (
                <div
                    className="absolute z-50 mt-2 p-4 bg-white border shadow-lg"
                    style={{
                        borderRadius: 'var(--radius-md)',
                        width: '320px',
                        maxHeight: '500px',
                        overflowY: 'auto'
                    }}
                >
                    {/* Custom Hex Input */}
                    <div className="mb-4">
                        <label className="block text-xs font-medium mb-2" style={{ color: 'var(--color-gray-600)' }}>
                            Custom Color (Hex)
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={customColor}
                                onChange={handleCustomColorChange}
                                placeholder="#000000"
                                className="flex-1 px-3 py-2 border border-gray-300 font-mono text-sm"
                                style={{ borderRadius: 'var(--radius-sm)' }}
                                maxLength={7}
                            />
                            <input
                                type="color"
                                value={customColor}
                                onChange={(e) => handleColorSelect(e.target.value)}
                                className="w-12 h-10 border-0 cursor-pointer"
                                style={{ borderRadius: 'var(--radius-sm)' }}
                            />
                        </div>
                    </div>

                    {/* Brand Colors */}
                    <div className="mb-4">
                        <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--color-gray-700)' }}>
                            Brand Colors
                        </h4>
                        <div className="grid grid-cols-6 gap-2">
                            {brandColors.map((item, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => handleColorSelect(item.color)}
                                    className="relative w-10 h-10 border-2 hover:scale-110 transition-transform"
                                    style={{
                                        backgroundColor: item.color,
                                        borderRadius: 'var(--radius-sm)',
                                        borderColor: value === item.color ? 'var(--color-primary)' : 'var(--color-gray-300)'
                                    }}
                                    title={item.name}
                                >
                                    {value === item.color && (
                                        <Check
                                            className="absolute inset-0 m-auto w-5 h-5"
                                            style={{
                                                color: item.color === '#FFFFFF' ? '#000000' : '#FFFFFF'
                                            }}
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Common Colors */}
                    <div className="mb-4">
                        <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--color-gray-700)' }}>
                            Common Colors
                        </h4>
                        <div className="grid grid-cols-10 gap-1.5">
                            {commonColors.map((color, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => handleColorSelect(color)}
                                    className="relative w-7 h-7 border hover:scale-110 transition-transform"
                                    style={{
                                        backgroundColor: color,
                                        borderRadius: 'var(--radius-sm)',
                                        borderColor: value === color ? 'var(--color-primary)' : 'transparent'
                                    }}
                                    title={color}
                                >
                                    {value === color && (
                                        <Check className="absolute inset-0 m-auto w-4 h-4 text-white" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Recent Colors */}
                    {showRecent && recentColors.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--color-gray-700)' }}>
                                Recent Colors
                            </h4>
                            <div className="flex gap-2 flex-wrap">
                                {recentColors.map((color, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => handleColorSelect(color)}
                                        className="relative w-8 h-8 border-2 hover:scale-110 transition-transform"
                                        style={{
                                            backgroundColor: color,
                                            borderRadius: 'var(--radius-sm)',
                                            borderColor: value === color ? 'var(--color-primary)' : 'var(--color-gray-300)'
                                        }}
                                        title={color}
                                    >
                                        {value === color && (
                                            <Check className="absolute inset-0 m-auto w-4 h-4 text-white" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Close Button */}
                    <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="absolute top-2 right-2 p-1 hover:bg-gray-100"
                        style={{ borderRadius: 'var(--radius-sm)' }}
                    >
                        <X className="w-4 h-4" style={{ color: 'var(--color-gray-500)' }} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default EnhancedColorPicker;
